// src/components/AdminOPDs.js
import React, { useEffect, useState, useMemo } from "react";
import { Pencil, Trash2, Search, Download, X, ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

export default function AdminOPDs() {
  const [opds, setOpds] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });

  const [showDetails, setShowDetails] = useState(false);
  const [detailOPD, setDetailOPD] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newOPD, setNewOPD] = useState({ contactEmail: "", password: "" });

  const [opdToEdit, setOpdToEdit] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [opdToDelete, setOpdToDelete] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    fetchOPDs();
  }, []);

  const fetchOPDs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/get_opds.php`);
      const data = await response.json();

      if (data.status === 'success') {
        setOpds(data.data);
      } else {
        console.error("Failed to fetch OPDs:", data.message);
        setOpds([]);
      }
    } catch (error) {
      console.error("Failed to fetch OPDs:", error);
      setOpds([]);
    }
    setLoading(false);
  };

  const filteredOPDs = useMemo(() => {
    return opds.filter((opd) => {
      if (
        searchTerm &&
        !opd.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
        return false;
      return true;
    });
  }, [opds, searchTerm]);

  const sortedOPDs = useMemo(() => {
    const sorted = [...filteredOPDs];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key] ?? "";
        const bVal = b[sortConfig.key] ?? "";
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [filteredOPDs, sortConfig]);

  const paginatedOPDs = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedOPDs.slice(start, start + itemsPerPage);
  }, [sortedOPDs, currentPage]);

  const totalPages = Math.ceil(sortedOPDs.length / itemsPerPage);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleDetail = (opd) => {
    setDetailOPD(opd);
    setShowDetails(true);
  };

  const closeDetailModal = () => {
    setShowDetails(false);
    setDetailOPD(null);
  };

  const handleEditClick = (opd) => {
    setOpdToEdit({
      ...opd,
      contact_email_original: opd.contact_email,
      newPassword: "",
    });
    setIsEditModalOpen(true);
  };


  const handleDeleteClick = (opd) => {
    setOpdToDelete(opd);
    setIsDeleteConfirmOpen(true);
  };

  const handleUpdateOPD = async (e) => {
    e.preventDefault();
    if (!opdToEdit || !opdToEdit.id) return;

    try {
      // Separate newPassword from the rest of the OPD fields
      const { newPassword, ...rest } = opdToEdit;
      const payload = {
        ...rest,
        old_email: opdToEdit.contact_email_original || opdToEdit.contact_email,
      };

      const res = await fetch(
        `${API_BASE_URL}/update_opd.php?id=${opdToEdit.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (data.status === "success" || data.status === "warning") {
        // Update local state with non-password fields
        setOpds((prev) =>
          prev.map((opd) =>
            opd.id === opdToEdit.id ? { ...opd, ...rest } : opd
          )
        );

        // If admin provided new password, reset it without requiring current password
        if (newPassword && newPassword.trim().length > 0) {
          try {
            const pwdRes = await fetch(
              `${API_BASE_URL}/admin_reset_opd_password.php`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email:
                    opdToEdit.contact_email_original ||
                    opdToEdit.contact_email,
                  new_password: newPassword,
                }),
              }
            );
            const pwdData = await pwdRes.json();
            if (pwdData.status === "success") {
              alert("Password reset successfully.");
            } else {
              alert(
                "Password reset failed: " +
                  (pwdData.message || "Unknown error")
              );
            }
          } catch (pwdErr) {
            console.error("Error resetting password:", pwdErr);
            alert("Server error while resetting password.");
          }
        }

        if (data.status === "success") {
          alert("OPD and associated user updated successfully.");
        } else if (data.status === "warning") {
          alert(data.message);
        }

        setIsEditModalOpen(false);
      } else {
        alert("" + (data.message || "Update failed"));
      }
    } catch (err) {
      console.error("Server error while updating OPD:", err);
      alert("Server error while updating OPD");
    }
  };


  const confirmDelete = async () => {
  if (!opdToDelete) return;

  try {
    const response = await fetch(`${API_BASE_URL}/delete_opd.php`, {
      method: "POST", // use POST for reliability
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: opdToDelete.id, _method: "DELETE" }), // send ID in body
    });

    const data = await response.json();

    if (data.status === "success") {
      setIsDeleteConfirmOpen(false);
      setOpdToDelete(null);
      fetchOPDs(); // refresh list
    } else {
      alert("Failed to delete OPD: " + data.message);
    }
  } catch (error) {
    console.error("Failed to delete OPD:", error);
    alert("Failed to delete OPD due to a network or server issue.");
  }
};


  const handleAddOPD = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/add_opd.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contactEmail: newOPD.contactEmail,
          password: newOPD.password
        }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setNewOPD({
          contactEmail: "",
          password: "",
        });
        setShowAddModal(false);
        fetchOPDs(); // Refresh data
      } else {
        alert('Failed to add OPD: ' + data.message);
      }
    } catch (error) {
      console.error("Error adding OPD:", error);
      alert('Failed to add OPD');
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800">OPD አስተዳደር/OPD Management</h1>
        <p className="text-gray-500 mt-1">Oversee all Organizations of Persons with Disabilities.</p>
      </header>

      {/* Control Panel */}
      <div className="bg-white p-6 rounded-xl shadow-md mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Search Input */}
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="search"
              placeholder="Search by OPD name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
            />
          </div>

          {/* Add OPD Button */}
          <div className="flex items-center justify-end">
            <button
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
              onClick={() => setShowAddModal(true)}
            >
              አዲስ ኦፒዲ አክል/Add New OPD
            </button>
          </div>
        </div>
      </div>

      {/* OPDs Table & Cards */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading ? (
          <p className="text-center text-gray-500 py-16">Loading OPDs...</p>
        ) : sortedOPDs.length === 0 ? (
          <p className="text-center text-gray-500 py-16">No OPDs found matching your criteria.</p>
        ) : (
          <>
            {/* Table for Medium screens and up */}
            <div className="overflow-x-auto hidden md:block">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">#</th>
                    {[
                      { key: 'name', label: 'Name' },
                      { key: 'contact_person', label: 'Contact Person' },
                      { key: 'contact_phone', label: 'Contact Phone' },
                    ].map(col => (
                      <th key={col.key} onClick={() => requestSort(col.key)} className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                        <div className="flex items-center gap-2">
                          {col.label}
                          {sortConfig.key === col.key ? (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          ) : <ChevronsUpDown className="w-4 h-4 text-gray-400" />}
                        </div>
                      </th>
                    ))}
                    <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedOPDs.map((opd, idx) => (
                    <tr key={opd.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-gray-600">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td className="py-3 px-4 text-gray-700 font-medium">{opd.name}</td>
                      <td className="py-3 px-4 text-gray-600">{opd.contact_person}</td>
                      <td className="py-3 px-4 text-gray-600">{opd.contact_phone}</td>
                      <td className="py-3 px-4 flex items-center gap-2">
                        <button className="px-3 py-1 bg-teal-600 text-white text-xs font-semibold rounded-full hover:bg-teal-700" onClick={() => handleDetail(opd)}>Details</button>
                        <button 
                          className="p-2 rounded-full hover:bg-gray-200 text-gray-600" 
                          onClick={() => handleEditClick(opd)} 
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button 
                          className="p-2 rounded-full hover:bg-red-100 text-red-600" 
                          onClick={() => handleDeleteClick(opd)} 
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards for Small screens */}
            <div className="block md:hidden">
              <div className="divide-y divide-gray-200">
                {paginatedOPDs.map((opd) => (
                  <div key={opd.id} className="p-4" onClick={() => handleDetail(opd)}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-bold text-gray-800">{opd.name}</p>
                        <p className="text-sm text-gray-500">Contact: {opd.contact_person}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button 
                          className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
                          onClick={(e) => { e.stopPropagation(); handleEditClick(opd); }}
                        >
                          <Pencil size={16} />
                        </button>
                        <button 
                          className="p-2 rounded-full hover:bg-red-100 text-red-600"
                          onClick={(e) => { e.stopPropagation(); handleDeleteClick(opd); }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className="flex flex-col md:flex-row justify-between items-center p-4 border-t border-gray-200 gap-4">
              <span className="text-sm text-gray-600">Showing {paginatedOPDs.length} of {sortedOPDs.length} OPDs</span>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
                <button
                  className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add OPD Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 relative">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 font-bold text-xl"
              onClick={() => setShowAddModal(false)}
            >
              ✕
            </button>

            <h3 className="text-2xl font-bold text-center mb-6 text-blue-700">Add New OPD</h3>

            <form
              onSubmit={handleAddOPD}
              className="space-y-4"
            >
              <div className="space-y-4">
                <input
                  type="email"
                  placeholder="Email"
                  value={newOPD.contactEmail || ""}
                  onChange={(e) => setNewOPD({ ...newOPD, contactEmail: e.target.value })}
                  required
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={newOPD.password || ""}
                  onChange={(e) => setNewOPD({ ...newOPD, password: e.target.value })}
                  required
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Add OPD
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit OPD Modal */}
      {isEditModalOpen && opdToEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 relative overflow-y-auto max-h-[90vh]">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 font-bold text-xl"
              onClick={() => setIsEditModalOpen(false)}
            >
              ✕
            </button>
            <h3 className="text-2xl font-bold text-center mb-6 text-blue-700">Edit OPD</h3>
            <form onSubmit={handleUpdateOPD} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Name"
                  value={opdToEdit.name || ""}
                  onChange={(e) =>
                    setOpdToEdit({ ...opdToEdit, name: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Contact Person"
                  value={opdToEdit.contact_person || ""}
                  onChange={(e) =>
                    setOpdToEdit({
                      ...opdToEdit,
                      contact_person: e.target.value,
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Contact Phone"
                  value={opdToEdit.contact_phone || ""}
                  onChange={(e) =>
                    setOpdToEdit({
                      ...opdToEdit,
                      contact_phone: e.target.value,
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                />
                <input
                  type="email"
                  placeholder="Contact Email"
                  value={opdToEdit.contact_email || ""}
                  onChange={(e) =>
                    setOpdToEdit({
                      ...opdToEdit,
                      contact_email: e.target.value,
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Address"
                  value={opdToEdit.address || ""}
                  onChange={(e) =>
                    setOpdToEdit({ ...opdToEdit, address: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Sub-City"
                  value={opdToEdit.sub_city || ""}
                  onChange={(e) =>
                    setOpdToEdit({ ...opdToEdit, sub_city: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                />
                <input
                  type="date"
                  placeholder="Established Date"
                  value={opdToEdit.established_date || ""}
                  onChange={(e) =>
                    setOpdToEdit({
                      ...opdToEdit,
                      established_date: e.target.value,
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              {/* Password reset (admin) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <input
                  type="password"
                  placeholder="New Password (leave blank to keep)"
                  value={opdToEdit.newPassword || ""}
                  onChange={(e) =>
                    setOpdToEdit({
                      ...opdToEdit,
                      newPassword: e.target.value,
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                />
                <div className="text-xs text-gray-500 flex items-center">
                  If you enter a new password here, it will be reset for this
                  OPD account.
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-400 text-white rounded"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteConfirmOpen && opdToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6">
            <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
            <p>Are you sure you want to delete the OPD "{opdToDelete.name}"? This will also delete the associated user account.</p>
            <div className="flex justify-end gap-4 mt-6">
              <button className="px-4 py-2 rounded bg-gray-300" onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</button>
              <button className="px-4 py-2 rounded bg-red-600 text-white" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}


      {/* Detail Modal */}
      {showDetails && detailOPD && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-y-auto max-h-[80vh] relative p-6 border border-gray-100">
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 font-bold text-xl"
              onClick={closeDetailModal}
            >
              ✕
            </button>

            {/* Modal Title */}
            <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">OPD Details</h3>
            <p className="text-sm text-gray-500 mb-6 text-center">Overview of the organizations registration and contact information.</p>

            {/* Key Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm">
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Name</p>
                <p className="mt-1 text-gray-900 font-semibold text-base">{detailOPD.name || "Not Assigned"}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm">
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Contact Person</p>
                <p className="mt-1 text-gray-900 font-semibold text-base">{detailOPD.contact_person || "Not Assigned"}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm">
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Contact Phone</p>
                <p className="mt-1 text-gray-900 font-semibold text-base">{detailOPD.contact_phone || "Not Assigned"}</p>
              </div>
            </div>

            {/* Other Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[ 
                { key: 'contact_email', label: 'Contact Email' },
                { key: 'address', label: 'Address' },
                { key: 'sub_city', label: 'Sub City' },
                { key: 'established_date', label: 'Established Date' },
                { key: 'created_at', label: 'Created At' },
                { key: 'updated_at', label: 'Updated At' },
              ].map(({ key, label }) => (
                <div
                  key={key}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition"
                >
                  <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">{label}</p>
                  <p className="mt-1 text-gray-900 font-semibold">
                    {detailOPD[key] ? detailOPD[key].toString() : 'Not Assigned'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
    <p className="text-gray-800 font-medium">{value || "-"}</p>
  </div>
);
